// ─── src/admin/components/ColorSelect.jsx ────────────────────────────────────
//
// Reusable color dropdown for admin pages.
// Shows all live theme colors (from Firestore) at the top,
// then any manually added custom colors below a divider.
// Lets the user type/pick a new hex color and add it to the list.
//
// Props:
//   value       — currently selected hex string
//   onChange    — (hex: string) => void
//   loading     — bool (passed from useThemeColors)
//   themeColors — [{ varName, hex, label }] from useThemeColors hook
//   storageKey  — string — localStorage key to persist custom colors per page
//                 e.g. 'admin_custom_colors_projects'

import React, { useState, useEffect } from 'react';
import { Select, Space, Button, Input, Divider, Tooltip, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

function isValidHex(str) {
  return /^#[0-9a-fA-F]{6}$/.test(str);
}

function isColorDark(hex) {
  if (!hex || hex.length < 7) return true;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

export default function ColorSelect({ value, onChange, themeColors = [], loading = false, storageKey = 'admin_custom_colors' }) {
  const [customColors, setCustomColors] = useState([]);
  const [inputHex,     setInputHex]     = useState('#');
  const [pickerHex,    setPickerHex]    = useState('#c8f04a');
  const [addMode,      setAddMode]      = useState(false);

  // ── Persist custom colors in localStorage per page ──────────────────────────
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) setCustomColors(JSON.parse(saved));
    } catch (_) {}
  }, [storageKey]);

  const persistCustom = (colors) => {
    setCustomColors(colors);
    try { localStorage.setItem(storageKey, JSON.stringify(colors)); } catch (_) {}
  };

  // ── Add a new custom color ──────────────────────────────────────────────────
  const handleAdd = () => {
    const hex = inputHex.trim().toUpperCase();
    if (!isValidHex(hex)) return;
    // Don't duplicate
    const allHexes = [
      ...themeColors.map(c => c.hex.toUpperCase()),
      ...customColors.map(c => c.toUpperCase()),
    ];
    if (allHexes.includes(hex)) {
      onChange(hex);
      setAddMode(false);
      return;
    }
    const updated = [...customColors, hex];
    persistCustom(updated);
    onChange(hex);
    setAddMode(false);
    setInputHex('#');
  };

  const handleRemoveCustom = (hex, e) => {
    e.stopPropagation();
    const updated = customColors.filter(c => c.toUpperCase() !== hex.toUpperCase());
    persistCustom(updated);
    // If the removed color was selected, clear selection
    if (value?.toUpperCase() === hex.toUpperCase()) onChange('');
  };

  // ── Sync picker → text input ────────────────────────────────────────────────
  const handlePickerChange = (e) => {
    setPickerHex(e.target.value);
    setInputHex(e.target.value.toUpperCase());
  };

  // ── Option renderer ─────────────────────────────────────────────────────────
  const renderOption = (hex, label, isCustom = false) => {
    const textColor = isColorDark(hex) ? '#f0ece4' : '#050505';
    return (
      <Select.Option key={hex} value={hex}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space size={8}>
            {/* Swatch */}
            <div style={{
              width: 20, height: 20, borderRadius: 3,
              background: hex, border: '1px solid rgba(0,0,0,0.15)',
              flexShrink: 0,
            }} />
            {/* Label pill */}
            <div style={{
              background: hex, color: textColor,
              padding: '1px 8px', borderRadius: 3,
              fontSize: 11, fontFamily: 'Space Mono, monospace',
              letterSpacing: '0.05em', lineHeight: '20px',
            }}>
              {label}
            </div>
            {/* Hex string */}
            <span style={{ fontSize: 12, color: '#888', fontFamily: 'Space Mono, monospace' }}>
              {hex.toUpperCase()}
            </span>
          </Space>
          {isCustom && (
            <span
              style={{ color: '#666', fontSize: 11, cursor: 'pointer', padding: '0 4px' }}
              onClick={e => handleRemoveCustom(hex, e)}
              title="Remove custom color"
            >
              ✕
            </span>
          )}
        </Space>
      </Select.Option>
    );
  };

  return (
    <Select
      value={value}
      onChange={onChange}
      loading={loading}
      style={{ width: '100%' }}
      placeholder="Select a color"
      optionLabelProp="label"
      // Custom label for the selected value trigger
      labelInValue={false}
      // Render selected value in the trigger
      optionRender={undefined}
      dropdownRender={(menu) => (
        <>
          {/* Theme colors header */}
          <div style={{
            padding: '6px 12px 4px',
            fontSize: 10, color: '#888',
            fontFamily: 'Space Mono, monospace',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            borderBottom: '1px solid #1e1e1e',
          }}>
            Theme Colors
          </div>

          {menu}

          {/* Custom colors section */}
          {customColors.length > 0 && (
            <div style={{
              padding: '6px 12px 4px',
              fontSize: 10, color: '#666',
              fontFamily: 'Space Mono, monospace',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              borderTop: '1px solid #1e1e1e',
              marginTop: 4,
            }}>
              Custom Colors
            </div>
          )}
          {customColors.map(hex => renderOption(hex, hex, true))}

          {/* Add custom color UI */}
          <div style={{ borderTop: '1px solid #1e1e1e', padding: '8px 12px' }}>
            {addMode ? (
              <Space size={6} wrap>
                {/* Native color picker */}
                <div style={{ position: 'relative', width: 32, height: 32 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 4,
                    background: pickerHex, border: '1px solid #333',
                  }} />
                  <input
                    type="color"
                    value={pickerHex}
                    onChange={handlePickerChange}
                    style={{
                      position: 'absolute', inset: 0,
                      opacity: 0, width: '100%', height: '100%',
                      cursor: 'pointer', padding: 0, border: 'none',
                    }}
                  />
                </div>
                <Input
                  size="small"
                  value={inputHex}
                  maxLength={7}
                  onChange={e => {
                    setInputHex(e.target.value.toUpperCase());
                    if (isValidHex(e.target.value)) setPickerHex(e.target.value);
                  }}
                  onPressEnter={handleAdd}
                  placeholder="#RRGGBB"
                  style={{
                    width: 100,
                    fontFamily: 'Space Mono, monospace',
                    fontSize: 12,
                    borderColor: isValidHex(inputHex) ? '#c8f04a' : '#d9d9d9',
                  }}
                />
                <Button
                  size="small"
                  type="primary"
                  onClick={handleAdd}
                  disabled={!isValidHex(inputHex)}
                  style={isValidHex(inputHex) ? { background: '#c8f04a', borderColor: '#c8f04a', color: '#000' } : {}}
                >
                  Add
                </Button>
                <Button size="small" onClick={() => { setAddMode(false); setInputHex('#'); }}>
                  Cancel
                </Button>
              </Space>
            ) : (
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setAddMode(true)}
                style={{ width: '100%', fontSize: 11 }}
              >
                Add custom color
              </Button>
            )}
          </div>
        </>
      )}
    >
      {/* Theme color options */}
      {themeColors.map(({ varName, hex, label }) => (
        <Select.Option key={hex} value={hex}>
          <Space size={8}>
            <div style={{
              width: 20, height: 20, borderRadius: 3,
              background: hex, border: '1px solid rgba(0,0,0,0.15)',
              flexShrink: 0,
            }} />
            <div style={{
              background: hex,
              color: isColorDark(hex) ? '#f0ece4' : '#050505',
              padding: '1px 8px', borderRadius: 3,
              fontSize: 11, fontFamily: 'Space Mono, monospace',
              letterSpacing: '0.05em', lineHeight: '20px',
            }}>
              {label}
            </div>
            <span style={{ fontSize: 11, color: '#888', fontFamily: 'Space Mono, monospace' }}>
              {varName}
            </span>
            <span style={{ fontSize: 12, color: '#aaa', fontFamily: 'Space Mono, monospace' }}>
              {hex.toUpperCase()}
            </span>
          </Space>
        </Select.Option>
      ))}
    </Select>
  );
}